import { BLACK_TEXT, WHITE } from 'constants/styling/theme'
import { useCallback, useEffect, useRef, useState } from 'react'

import { DEFAULT_BRUSH_SIZE } from './MaskDrawer.controller'
import constate from 'constate'

/** Enum representing the navigation paths for the ClientAIStudioNavTabs component. */
export enum NavigationPaths {
  VISUAL_EDITING = 'VISUAL_EDITING',
  VIRTUAL_STAGING = 'VIRTUAL_STAGING',
}

export const [MaskDrawerProvider, useMaskDrawer] = constate(({
  imageUrl,
}: {
  imageUrl: string
}) => {
  // TODO add analytics event logging
  // const { logAIStudioEvent } = useClientAIStudio()

  const imageRef = useRef<HTMLImageElement>(null)
  const previewMaskCanvasRef = useRef<HTMLCanvasElement>(null)
  const storedMaskCanvasRef = useRef<HTMLCanvasElement>(null)
  const previewImageSizeRef = useRef<{ width: number; height: number }>({ width: 0, height: 0 })
  // Store the last position to draw continuous lines
  const lastPosRef = useRef<{ x: number; y: number } | null>(null)

  const undoStack = useRef<ImageData[]>([])
  const redoStack = useRef<ImageData[]>([])

  const [previewImageMagicEraser, setPreviewImageMagicEraser] = useState<string>(imageUrl)
  const [brushSize, setBrushSize] = useState(DEFAULT_BRUSH_SIZE)
  const [isDrawing, setIsDrawing] = useState(false)
  const [maskUrl, setMaskUrl] = useState<string | null>(null)
  const [canvasReady, setCanvasReady] = useState(false)
  const [historyVersion, setHistoryVersion] = useState(0) // Used to force re-render when stacks change

  const syncCanvasSize = () => {
    const image = imageRef.current!
    const previewMaskCanvas = previewMaskCanvasRef.current!
    // Set canvas size to image's natural size for sharpness
    previewMaskCanvas.width = image.naturalWidth
    previewMaskCanvas.height = image.naturalHeight

    // Scale canvas display size to match image's rendered size
    previewMaskCanvas.style.width = `${image.clientWidth}px`
    previewMaskCanvas.style.height = `${image.clientHeight}px`

    const storedMaskCanvas = storedMaskCanvasRef.current!
    storedMaskCanvas.width = image.naturalWidth
    storedMaskCanvas.height = image.naturalHeight
    const storedMaskCtx = storedMaskCanvas.getContext('2d')!
    storedMaskCtx.clearRect(0, 0, storedMaskCanvas.width, storedMaskCanvas.height)

    const previewMaskCtx = previewMaskCanvas.getContext('2d')!
    previewMaskCtx.clearRect(0, 0, previewMaskCanvas.width, previewMaskCanvas.height)

    setCanvasReady(true)
  }

  const getCanvasCoords = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = previewMaskCanvasRef.current!
    const rect = canvas.getBoundingClientRect()
    // Adjust coordinates to match the actual canvas resolution
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    }
  }

  const saveSnapshot = () => {
    const ctx = storedMaskCanvasRef.current!.getContext('2d')!
    const imageData = ctx.getImageData(0, 0, storedMaskCanvasRef.current!.width, storedMaskCanvasRef.current!.height)
    undoStack.current.push(imageData)
    redoStack.current = []
    setHistoryVersion(v => v + 1)
  }

  const restoreSnapshot = (imageData: ImageData) => {
    const ctx = storedMaskCanvasRef.current!.getContext('2d')!
    ctx.putImageData(imageData, 0, 0)
    redrawPreview()
  }

  const redrawPreview = () => {
    const previewCtx = previewMaskCanvasRef.current!.getContext('2d')!
    const previewCanvas = previewMaskCanvasRef.current!
    previewCtx.clearRect(0, 0, previewCanvas.width, previewCanvas.height)

    // Step 1: Draw the white strokes from mask
    previewCtx.globalAlpha = 1
    previewCtx.drawImage(storedMaskCanvasRef.current!, 0, 0)

    // Step 2: Apply coral tint using source-in blend
    previewCtx.globalCompositeOperation = 'source-in'
    previewCtx.fillStyle = 'rgba(255, 90, 95, 0.86)' // CORAL_600 with 86% opacity
    previewCtx.fillRect(0, 0, previewCanvas.width, previewCanvas.height)

    // Step 3: Reset blend mode
    previewCtx.globalCompositeOperation = 'source-over'
    previewCtx.globalAlpha = 1
  }

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const { x, y } = getCanvasCoords(e)

    const previewMaskctx = previewMaskCanvasRef.current!.getContext('2d')!
    const storedMaskCtx = storedMaskCanvasRef.current!.getContext('2d')!

    // --- Draw solid white into stored mask ---
    storedMaskCtx.strokeStyle = WHITE
    storedMaskCtx.lineWidth = brushSize
    storedMaskCtx.lineCap = 'round'
    storedMaskCtx.lineJoin = 'round'

    if (lastPosRef.current) {
      storedMaskCtx.beginPath()
      storedMaskCtx.moveTo(lastPosRef.current.x, lastPosRef.current.y)
      storedMaskCtx.lineTo(x, y)
      storedMaskCtx.stroke()
    } else {
      storedMaskCtx.beginPath()
      storedMaskCtx.moveTo(x, y)
      storedMaskCtx.lineTo(x + 0.1, y + 0.1)
      storedMaskCtx.stroke()
    }

    // --- Render coral preview from white mask ---
    const previewCanvas = previewMaskCanvasRef.current!
    previewMaskctx.clearRect(0, 0, previewCanvas.width, previewCanvas.height)

    previewMaskctx.lineWidth = brushSize
    previewMaskctx.lineCap = 'round'
    previewMaskctx.lineJoin = 'round'

    // Step 1: Draw the white strokes from mask
    previewMaskctx.globalAlpha = 1
    previewMaskctx.drawImage(storedMaskCanvasRef.current!, 0, 0)

    // Step 2: Apply coral tint using source-in blend
    previewMaskctx.globalCompositeOperation = 'source-in'
    previewMaskctx.fillStyle = 'rgba(255, 90, 95, 0.86)' // CORAL_600 with 86% opacity
    previewMaskctx.fillRect(0, 0, previewCanvas.width, previewCanvas.height)

    // Step 3: Reset blend mode
    previewMaskctx.globalCompositeOperation = 'source-over'
    previewMaskctx.globalAlpha = 1

    lastPosRef.current = { x, y }
  }

  // Reset lastPosRef on mouse up/leave
  useEffect(() => {
    if (!isDrawing) lastPosRef.current = null
  }, [isDrawing])

  const onUndo = useCallback(() => {
    if (undoStack.current.length === 0) return
    const last = undoStack.current.pop()!
    const ctx = storedMaskCanvasRef.current!.getContext('2d')!
    const currentData = ctx.getImageData(0, 0, storedMaskCanvasRef.current!.width, storedMaskCanvasRef.current!.height)
    redoStack.current.push(currentData)
    restoreSnapshot(last)
    setHistoryVersion(v => v + 1)

    // skip restoreSnapshot
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onRedo = useCallback(() => {
    if (redoStack.current.length === 0) return
    const redoData = redoStack.current.pop()!
    const ctx = storedMaskCanvasRef.current!.getContext('2d')!
    const currentData = ctx.getImageData(0, 0, storedMaskCanvasRef.current!.width, storedMaskCanvasRef.current!.height)
    undoStack.current.push(currentData)
    restoreSnapshot(redoData)
    setHistoryVersion(v => v + 1)

    // skip restoreSnapshot
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleExport = (blob: Blob) => {
    const url = URL.createObjectURL(blob)
    setMaskUrl(url)
  }

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    saveSnapshot()
    setIsDrawing(true)
    draw(e)
  }
  const handleMouseUp = () => setIsDrawing(false)
  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (isDrawing) draw(e)
  }

  const exportMask = () => {
    const maskCanvas = storedMaskCanvasRef.current!
    const { width, height } = previewImageSizeRef.current

    const exportCanvas = document.createElement('canvas')
    exportCanvas.width = width
    exportCanvas.height = height

    const exportCtx = exportCanvas.getContext('2d')!
    // Fill black background for mask image export
    exportCtx.fillStyle = BLACK_TEXT
    exportCtx.fillRect(0, 0, width, height)

    // Scale and draw white strokes from stored canvas
    exportCtx.drawImage(maskCanvas, 0, 0)

    exportCanvas.toBlob((blob) => {
      if (blob) handleExport(blob)
    }, 'image/png')
  }

  return {
    previewImageMagicEraser,
    setPreviewImageMagicEraser,
    saveSnapshot,
    restoreSnapshot,
    exportMask,
    onUndo,
    onRedo,
    brushSize,
    imageRef,
    previewMaskCanvasRef,
    storedMaskCanvasRef,
    isDrawing,
    setIsDrawing,
    getCanvasCoords,
    syncCanvasSize,
    redrawPreview,
    maskUrl,
    setMaskUrl,
    canvasReady,
    historyVersion,
    setBrushSize,
    handleExport,
    handleMouseDown,
    handleMouseUp,
    handleMouseMove,
    undoStack,
    redoStack,
    previewImageSizeRef,
  }
})
